/**
 * Responsive Breakpoint Utilities
 * Provides breakpoint detection and responsive helpers for mobile support
 */

import { useState, useEffect } from 'react';

/**
 * Breakpoint definitions (in pixels)
 *
 * Portrait: < 480px (very small phones)
 * Mobile: 480px - 767px (phones landscape)
 * Tablet: 768px - 1023px (tablets)
 * Desktop: 1024px - 1535px (laptops/standard monitors)
 * Wide: ≥ 1536px (large monitors, 4K displays)
 */
export const BREAKPOINTS = {
  MOBILE_PORTRAIT: 480,
  MOBILE_LANDSCAPE: 768,
  TABLET: 1024,
  WIDE: 1536,
};

/**
 * Get current breakpoint based on window width
 * @returns {string} 'portrait' | 'mobile' | 'tablet' | 'desktop' | 'wide'
 */
export function getCurrentBreakpoint() {
  const width = document.documentElement.clientWidth;

  if (width < BREAKPOINTS.MOBILE_PORTRAIT) {
    return 'portrait';
  } else if (width < BREAKPOINTS.MOBILE_LANDSCAPE) {
    return 'mobile';
  } else if (width < BREAKPOINTS.TABLET) {
    return 'tablet';
  } else if (width < BREAKPOINTS.WIDE) {
    return 'desktop';
  } else {
    return 'wide';
  }
}

/**
 * Check if current viewport is portrait (< 480px)
 * @returns {boolean}
 */
export function isPortrait() {
  return getCurrentBreakpoint() === 'portrait';
}

/**
 * Check if current viewport is mobile (portrait or landscape < 768px)
 * @returns {boolean}
 */
export function isMobile() {
  const bp = getCurrentBreakpoint();
  return bp === 'portrait' || bp === 'mobile';
}

/**
 * Check if current viewport is mobile landscape (480px - 767px)
 * @returns {boolean}
 */
export function isMobileLandscape() {
  return getCurrentBreakpoint() === 'mobile';
}

/**
 * Check if current viewport is tablet (768px - 1023px)
 * @returns {boolean}
 */
export function isTablet() {
  return getCurrentBreakpoint() === 'tablet';
}

/**
 * Check if current viewport is tablet or smaller (< 1024px)
 * Useful for responsive layouts that need to adapt for non-desktop screens
 * @returns {boolean}
 */
export function isTabletOrSmaller() {
  const bp = getCurrentBreakpoint();
  return bp === 'portrait' || bp === 'mobile' || bp === 'tablet';
}

/**
 * Check if current viewport is desktop (1024px - 1535px)
 * @returns {boolean}
 */
export function isDesktop() {
  return getCurrentBreakpoint() === 'desktop';
}

/**
 * Check if current viewport is wide (≥ 1536px)
 * @returns {boolean}
 */
export function isWide() {
  return getCurrentBreakpoint() === 'wide';
}

/**
 * React hook for reactive breakpoint detection
 * Automatically updates when window is resized (throttled with requestAnimationFrame)
 *
 * @returns {string} Current breakpoint: 'portrait' | 'mobile' | 'tablet' | 'desktop' | 'wide'
 *
 * @example
 * function MyComponent() {
 *   const breakpoint = useBreakpoint();
 *   const isMobileView = breakpoint === 'portrait' || breakpoint === 'mobile' || breakpoint === 'tablet';
 *   return <div>Current breakpoint: {breakpoint}</div>;
 * }
 */
export function useBreakpoint() {
  const [breakpoint, setBreakpoint] = useState(getCurrentBreakpoint());

  useEffect(() => {
    let rafId = null;

    const handleResize = () => {
      // Throttle using requestAnimationFrame - only update once per frame
      if (rafId) return; // Skip if already scheduled

      rafId = requestAnimationFrame(() => {
        
        const newBreakpoint = getCurrentBreakpoint();
        /*console.log('[RAF] Executing:', {
          viewportWidth: currentWidth,
          calculatedBreakpoint: newBreakpoint
        });*/
        setBreakpoint(newBreakpoint);
        rafId = null;
      });
    };

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return breakpoint;
}
